import type { SupportedPlatforms } from '~/constants/platforms';
import type {
  CreatorAudienceDemographicsStatusCode,
  CreatorChannelMetricsStatusCode,
  CreatorOverallStatusCode,
  CreatorPlatformDataStatus,
} from '~/hooks/api/creators/useGetCreatorPlatformStatus';

type EmptyStateTarget = 'all' | 'demographics' | 'metrics';
type EmptyStateButtonType = 'connect' | 'addLocation';

interface EmptyStateEntry {
  message: string;
  target: EmptyStateTarget;
  buttonType?: EmptyStateButtonType;
}

const MANAGER_EMPTY_STATES = {
  notConnected: {
    message:
      "Connect the talent's account to view real-time metrics and audience insights.",
    target: 'all',
    buttonType: 'connect',
  },
  retrievingMetrics: {
    message:
      "We're working on retrieving the metrics. Please allow up to 24 hours for it to appear.",
    target: 'all',
  },
  updatingMetrics: {
    message:
      "We're still updating the metrics. Please allow up to 24 hours for the latest data to appear.",
    target: 'all',
  },
  metricsUnavailable: {
    message: 'The metrics are currently unavailable. Please check back later.',
    target: 'all',
  },
  belowMinimumFollowers: {
    message: 'Metrics will appear once the talent reaches 100 followers.',
    target: 'demographics',
  },
  locationNotSupported: {
    message:
      'Audience location estimates are not yet supported for talent from this region. Invite them to connect to view verified metrics.',
    target: 'demographics',
    buttonType: 'connect',
  },
  locationMissing: {
    message: "Add the talent's location to view audience location estimates.",
    target: 'demographics',
    buttonType: 'addLocation',
  },
  noRecentContent: {
    message:
      "Metrics are unavailable because the talent hasn't posted content in the past 30 days.",
    target: 'metrics',
  },
} as const satisfies Record<string, EmptyStateEntry>;

const CREATOR_EMPTY_STATES = {
  notConnected: {
    message:
      'Connect your account to view real-time metrics and audience insights.',
    target: 'all',
    buttonType: 'connect',
  },
  retrievingMetrics: {
    message:
      "We're working on retrieving the metrics. Please allow up to 24 hours for it to appear.",
    target: 'all',
  },
  updatingMetrics: {
    message:
      "We're still updating the metrics. Please allow up to 24 hours for the latest data to appear.",
    target: 'all',
  },
  metricsUnavailable: {
    message: 'The metrics are currently unavailable. Please check back later.',
    target: 'all',
  },
  belowMinimumFollowers: {
    message: 'Metrics will appear once you reach 100 followers.',
    target: 'demographics',
  },
  noRecentContent: {
    message:
      'Metrics are unavailable because no content has been posted in the past 30 days.',
    target: 'metrics',
  },
} as const satisfies Record<string, EmptyStateEntry>;

export type ManagerEmptyStateKey = keyof typeof MANAGER_EMPTY_STATES;
export type CreatorEmptyStateKey = keyof typeof CREATOR_EMPTY_STATES;

export const MANAGER_MESSAGES: Record<ManagerEmptyStateKey, EmptyStateEntry> =
  MANAGER_EMPTY_STATES;

export const CREATOR_MESSAGES: Record<CreatorEmptyStateKey, EmptyStateEntry> =
  CREATOR_EMPTY_STATES;

export type { EmptyStateEntry, EmptyStateTarget, EmptyStateButtonType };

/**
 * Codes from `GET /api/creators/{id}/status` that map onto an empty state.
 * `followerGrowth` codes are deliberately excluded: the growth-rate chart reads
 * the timeline endpoint's own status (see `getGrowthRatePresentation`).
 */
type EmptyStateStatusCode =
  | CreatorOverallStatusCode
  | CreatorAudienceDemographicsStatusCode
  | CreatorChannelMetricsStatusCode;

/**
 * Status code → copy key. Mirrors the legacy `ManagerStatusMessageStrategy`
 * on the API 1:1 where a code existed there; codes the legacy benchmark
 * `status` never produced map to the nearest existing copy.
 *
 * Both tables are exhaustive over `EmptyStateStatusCode`, so a new backend
 * code is a compile error here rather than a silently missing banner.
 */
const MANAGER_EMPTY_STATE_BY_CODE = {
  ACCOUNT_STATE_UNVERIFIED: 'notConnected',
  ACCOUNT_STATE_HANDLE_NOT_ADDED: 'notConnected',
  ACCOUNT_STATE_INITIAL_FETCH: 'retrievingMetrics',
  ACCOUNT_STATE_HANDLE_JUST_ADDED: 'retrievingMetrics',
  ACCOUNT_STATE_UPDATING_DATA: 'updatingMetrics',
  METRICS_UNAVAILABLE_UNKNOWN_ERROR: 'metricsUnavailable',
  SNAP_STAR_REQUIRED: 'metricsUnavailable',
  BRAND_DEALS_DISABLED: 'metricsUnavailable',
  DEMOGRAPHICS_UNAVAILABLE_LOW_FOLLOWERS: 'belowMinimumFollowers',
  STM_REGION_NOT_SUPPORTED: 'locationNotSupported',
  STM_LOCATION_MISSING: 'locationMissing',
  METRICS_UNAVAILABLE_NO_RECENT_CONTENT: 'noRecentContent',
} as const satisfies Record<EmptyStateStatusCode, ManagerEmptyStateKey>;

/**
 * Creators have no location-related copy (the legacy creator strategy returned
 * `null` for those codes), so the STM codes intentionally resolve to nothing.
 */
const CREATOR_EMPTY_STATE_BY_CODE = {
  ACCOUNT_STATE_UNVERIFIED: 'notConnected',
  ACCOUNT_STATE_HANDLE_NOT_ADDED: 'notConnected',
  ACCOUNT_STATE_INITIAL_FETCH: 'retrievingMetrics',
  ACCOUNT_STATE_HANDLE_JUST_ADDED: 'retrievingMetrics',
  ACCOUNT_STATE_UPDATING_DATA: 'updatingMetrics',
  METRICS_UNAVAILABLE_UNKNOWN_ERROR: 'metricsUnavailable',
  SNAP_STAR_REQUIRED: 'metricsUnavailable',
  BRAND_DEALS_DISABLED: 'metricsUnavailable',
  DEMOGRAPHICS_UNAVAILABLE_LOW_FOLLOWERS: 'belowMinimumFollowers',
  STM_REGION_NOT_SUPPORTED: undefined,
  STM_LOCATION_MISSING: undefined,
  METRICS_UNAVAILABLE_NO_RECENT_CONTENT: 'noRecentContent',
} as const satisfies Record<
  EmptyStateStatusCode,
  CreatorEmptyStateKey | undefined
>;

/**
 * Pick the single code that should drive the tab's empty state.
 *
 * `overall` blocks the whole platform and always wins. Otherwise demographics
 * win over channel metrics — the legacy API could only emit one message and
 * ordered "low followers" ahead of "no recent content"; `PlatformCharts` still
 * renders a single `EmptyStateEntry`, so we keep that precedence.
 */
const selectEmptyStateCode = (
  platformStatus: CreatorPlatformDataStatus,
): EmptyStateStatusCode | undefined =>
  platformStatus.overall?.code ??
  platformStatus.audienceDemographics?.code ??
  platformStatus.channelMetrics?.code ??
  undefined;

const STM_CODES: readonly EmptyStateStatusCode[] = [
  'STM_REGION_NOT_SUPPORTED',
  'STM_LOCATION_MISSING',
];

/**
 * Reproduce what the retired `BenchmarkStatusService` would have emitted for
 * this platform, given what `/status` reports. The new endpoint returns codes
 * in several situations where the legacy service deliberately returned
 * nothing; this keeps the on-screen behaviour identical (FOAM-22556).
 *
 * Legacy rules, per `krill-base-api` `Service/Benchmark/Status/*`:
 *  - IG/TT/YT: nothing unless the platform is `connected` with a valid token
 *    or `not-connected` (i.e. disconnected / expired tokens → nothing).
 *  - IG off-platform/manual: only the two STM location messages.
 *  - TT/YT off-platform/manual: nothing at all.
 *  - Snapchat: nothing when `not-connected`; otherwise only initial-fetch,
 *    updating-data and low-followers (no "not connected", no recent-content).
 *  - TikTok never reported low followers; Snapchat never reported no recent
 *    content; "unknown error" was hard-wired to `false` everywhere.
 *  - A missing handle read as "not connected"; a just-added handle had no
 *    special case (it was either "not connected" or "initial fetch").
 *  - No SNAP_STAR_REQUIRED / BRAND_DEALS_DISABLED equivalents existed.
 */
const toLegacyStatusCode = (
  platform: SupportedPlatforms,
  platformStatus: CreatorPlatformDataStatus,
): EmptyStateStatusCode | undefined => {
  const code = selectEmptyStateCode(platformStatus);
  if (!code) {
    return undefined;
  }

  const { connectionStatus, tokenValid } = platformStatus;
  const isNotConnected = connectionStatus === 'not-connected';
  const isConnectedWithValidToken =
    connectionStatus === 'connected' && tokenValid !== false;
  const isOffPlatformOrManual =
    connectionStatus === 'off-platform' || connectionStatus === 'manual';

  // Codes the legacy service had no equivalent for.
  if (
    code === 'SNAP_STAR_REQUIRED' ||
    code === 'BRAND_DEALS_DISABLED' ||
    code === 'METRICS_UNAVAILABLE_UNKNOWN_ERROR'
  ) {
    return undefined;
  }

  const normalized: EmptyStateStatusCode =
    code === 'ACCOUNT_STATE_HANDLE_NOT_ADDED'
      ? 'ACCOUNT_STATE_UNVERIFIED'
      : code === 'ACCOUNT_STATE_HANDLE_JUST_ADDED'
        ? isNotConnected
          ? 'ACCOUNT_STATE_UNVERIFIED'
          : 'ACCOUNT_STATE_INITIAL_FETCH'
        : code;

  if (platform === 'snapchat') {
    if (isNotConnected) {
      return undefined;
    }
    return normalized === 'ACCOUNT_STATE_INITIAL_FETCH' ||
      normalized === 'ACCOUNT_STATE_UPDATING_DATA' ||
      normalized === 'DEMOGRAPHICS_UNAVAILABLE_LOW_FOLLOWERS'
      ? normalized
      : undefined;
  }

  if (isOffPlatformOrManual) {
    return platform === 'instagram' && STM_CODES.includes(normalized)
      ? normalized
      : undefined;
  }

  if (!isConnectedWithValidToken && !isNotConnected) {
    // disconnected, or connected with an expired token
    return undefined;
  }

  if (STM_CODES.includes(normalized)) {
    // Legacy only produced STM messages on the off-platform branch above.
    return undefined;
  }

  if (
    platform === 'tiktok' &&
    normalized === 'DEMOGRAPHICS_UNAVAILABLE_LOW_FOLLOWERS'
  ) {
    return undefined;
  }

  return normalized;
};

export const getEmptyStateByStatusCode = (
  platform: SupportedPlatforms,
  platformStatus: CreatorPlatformDataStatus | undefined,
  isCreator: boolean,
): EmptyStateEntry | undefined => {
  if (!platformStatus) {
    return undefined;
  }

  const code = toLegacyStatusCode(platform, platformStatus);
  if (!code) {
    return undefined;
  }

  if (isCreator) {
    const key = CREATOR_EMPTY_STATE_BY_CODE[code];
    return key ? CREATOR_MESSAGES[key] : undefined;
  }

  return MANAGER_MESSAGES[MANAGER_EMPTY_STATE_BY_CODE[code]];
};
